import React, { useState, useEffect } from 'react';
import { Archive } from 'lucide-react';
import api from '../utils/api';
import { Navigation } from '../components/Navigation';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  ReferenceArea,
  Cell
} from 'recharts';

const Montor = () => {
  const [region, setRegion] = useState('');

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6" data-testid="montor-page">
      <Navigation />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Montører</h1>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
          data-testid="montor-region-select"
        >
          <option value="">Hele landet</option>
          <option value="Sjælland">Sjælland</option>
          <option value="Fyn">Fyn</option>
          <option value="Jylland">Jylland</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <TimerSammenligning region={region} />
      </div>
    </div>
  );
};

const monthNames = ['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];
const now = new Date();
const currentLabel = `${monthNames[now.getMonth()]} ${String(now.getFullYear()).slice(-2)}`;

// Antal hverdage (Man-Fre) i en given måned
const countWorkdaysInMonth = (year, month) => {
  let count = 0;
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= lastDay; day++) {
    const wd = new Date(year, month, day).getDay();
    if (wd >= 1 && wd <= 5) count++;
  }
  return count;
};

// Parse periode-label "Maj 26" → {year, month}
const parsePeriodLabel = (label) => {
  if (!label || typeof label !== 'string') return null;
  const [m, yy] = label.split(' ');
  const monthIdx = monthNames.indexOf(m);
  if (monthIdx < 0 || !yy) return null;
  return { year: 2000 + parseInt(yy, 10), month: monthIdx };
};

const TimerTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const k = payload.find(p => p.dataKey === 'konsulent_timer')?.value || 0;
  const m = payload.find(p => p.dataKey === 'montor_timer')?.value || 0;
  const pct = payload.find(p => p.dataKey === 'forskel_pct')?.value || 0;
  const gap = k - m;
  let missingText = null;
  const parsed = parsePeriodLabel(label);
  if (parsed && gap > 0) {
    const workdays = countWorkdaysInMonth(parsed.year, parsed.month);
    const hoursPerMontorMonth = (workdays / 5) * 37;
    const missing = gap / hoursPerMontorMonth;
    missingText = `Mangler ${missing.toFixed(1).replace('.', ',')} montør(er) à 37 t/uge`;
  } else if (parsed && gap < 0) {
    missingText = `Overskud på ${(-gap).toLocaleString('da-DK')} montør-timer`;
  }
  return (
    <div style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color: '#0288D1' }}>Konsulent timer: {k.toLocaleString('da-DK')} t</div>
      <div style={{ color: '#FF6F00' }}>Montør timer: {m.toLocaleString('da-DK')} t</div>
      <div style={{ color: '#EF5350', marginTop: 4 }}>Forskel: {pct >= 0 ? '+' : ''}{pct}%</div>
      {missingText && (
        <div style={{ color: 'rgba(255,255,255,0.85)', marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', fontWeight: 500 }}>
          {missingText}
        </div>
      )}
    </div>
  );
};

const TimerSammenligning = ({ region }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = region ? `?region=${encodeURIComponent(region)}` : '';
        const res = await api.get(`/montor/timer-sammenligning${params}`);
        const raw = res.data.data || [];
        const enriched = raw.map(d => {
          const diff = d.konsulent_timer > 0 ? ((d.montor_timer - d.konsulent_timer) / d.konsulent_timer) * 100 : 0;
          return { ...d, forskel_pct: Math.round(diff * 10) / 10 };
        });
        setData(enriched);
      } catch (e) {
        console.error('Error fetching timer data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [region]);

  return (
    <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="timer-sammenligning-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-white/80">Konsulent timer vs. Montør timer (6 mdr. tilbage + 3 mdr. frem)</h2>
      </div>
      {loading ? (
        <div className="h-80 flex items-center justify-center text-white/40">Indlæser...</div>
      ) : (
        <>
          <div className="flex flex-col xl:flex-row gap-4">
            <div className="xl:w-3/5 xl:flex-shrink-0 min-w-0">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="period" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} tickFormatter={(v) => `${v} t`} />
                  <Tooltip content={<TimerTooltip />} />
                  <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} payload={[
                    { value: 'Konsulent timer', type: 'line', color: '#0288D1' },
                    { value: 'Montør timer', type: 'line', color: '#FF6F00' },
                  ]} />
                  <YAxis yAxisId="pct" hide />
                  <Line isAnimationActive={false} type="monotone" dataKey="konsulent_timer" name="Konsulent timer" stroke="#0288D1" strokeWidth={2.5} dot={{ fill: '#0288D1', r: 4 }} activeDot={{ r: 6 }} />
                  <Line isAnimationActive={false} type="monotone" dataKey="montor_timer" name="Montør timer" stroke="#FF6F00" strokeWidth={2.5} dot={{ fill: '#FF6F00', r: 4 }} activeDot={{ r: 6 }} />
                  <Line isAnimationActive={false} yAxisId="pct" type="monotone" dataKey="forskel_pct" name="Forskel" stroke="#EF5350" strokeWidth={0} dot={false} activeDot={false} legendType="none" />
                  <ReferenceLine x={currentLabel} stroke="rgba(255,255,255,0.4)" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: 'Nu', position: 'top', fill: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600 }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-0.5">
                <p className="text-xs text-white/30"><span className="inline-block w-3 h-0.5 align-middle mr-1" style={{ background: '#0288D1' }}></span> Konsulent timer (fratrukket fravær, ekskl. konsulent/montør)</p>
                <p className="text-xs text-white/30"><span className="inline-block w-3 h-0.5 align-middle mr-1" style={{ background: '#FF6F00' }}></span> Montør timer (fratrukket fravær)</p>
              </div>
            </div>

            <div className="xl:w-2/5 xl:flex-shrink-0 xl:border-l xl:border-white/5 xl:pl-4 space-y-3 max-h-[460px] overflow-y-auto" data-testid="timer-details-panel">
              {data.map((d) => (
                <div key={d.period} className="bg-[#0d0d0d] rounded-lg border border-white/5">
                  <button
                    onClick={() => setExpandedPeriod(expandedPeriod === d.period ? null : d.period)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                    data-testid={`period-toggle-${d.period}`}
                  >
                    <span className="text-sm font-medium text-white/80 flex items-center gap-2">
                      {d.period}
                      {((d.snapshot_count_konsulenter || 0) + (d.snapshot_count_montoerer || 0)) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 bg-purple-500/15 border border-purple-400/30 text-purple-300 text-[10px] px-2 py-0.5 rounded-full"
                          title={`${(d.snapshot_count_konsulenter || 0) + (d.snapshot_count_montoerer || 0)} række(r) fra arkiv (kalender fjernet)`}
                          data-testid={`snapshot-badge-${d.period}`}
                        >
                          <Archive className="w-3 h-3" />
                          {(d.snapshot_count_konsulenter || 0) + (d.snapshot_count_montoerer || 0)} arkiv
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span style={{ color: '#0288D1' }}>{d.konsulenter?.length || 0}k · {d.konsulent_timer}t</span>
                      <span style={{ color: '#FF6F00' }}>{d.montoerer?.length || 0}m · {d.montor_timer}t</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-white/40 transition-transform ${expandedPeriod === d.period ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </button>
                  {expandedPeriod === d.period && (
                    <div className="px-3 pb-3 grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: '#0288D1' }}>Konsulenter</p>
                        {(d.konsulenter || []).map((k) => (
                          <div key={k.navn} className={`flex justify-between text-xs py-1 border-b border-white/5 ${k.from_snapshot ? 'bg-purple-500/5 px-1 rounded' : ''}`}>
                            <span className="text-white/60 truncate pr-1 flex items-center gap-1">
                              {k.from_snapshot && <Archive className="w-2.5 h-2.5 text-purple-300 flex-shrink-0" />}
                              {k.navn}
                            </span>
                            <span className="text-white/80 whitespace-nowrap">{k.timer} t</span>
                          </div>
                        ))}
                        {(!d.konsulenter || d.konsulenter.length === 0) && <p className="text-xs text-white/30">Ingen data</p>}
                      </div>
                      <div>
                        <p className="text-xs font-medium mb-2" style={{ color: '#FF6F00' }}>Montører</p>
                        {(d.montoerer || []).map((m) => (
                          <div key={m.navn} className={`flex justify-between text-xs py-1 border-b border-white/5 ${m.from_snapshot ? 'bg-purple-500/5 px-1 rounded' : ''}`}>
                            <span className="text-white/60 truncate pr-1 flex items-center gap-1">
                              {m.from_snapshot && <Archive className="w-2.5 h-2.5 text-purple-300 flex-shrink-0" />}
                              {m.navn}
                            </span>
                            <span className="text-white/80 whitespace-nowrap">{m.timer} t</span>
                          </div>
                        ))}
                        {(!d.montoerer || d.montoerer.length === 0) && <p className="text-xs text-white/30">Ingen data</p>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Montor;
